// qchat_fe/src/Component/SideData/Overview/AiAgentWorkFlow/components/ScopeTabs.jsx
//
// ============================================================
// ScopeTabs — the 🏢 Tenant Level / 🏠 App Level tab control
// ============================================================
//
// Shared by every resource screen that participates in the opt-in
// attachment model:
//
//   • APIConfigIndex.js
//   • ChainApis.jsx
//   • AuthIntegration.js
//   • OAuthConnections.jsx
//   • ConnectorDefinitionsPanel.jsx
//
// The tab bar is presentational. It renders the two tabs, the
// counts, and the "overridden by App Level" advisory banner. It
// does NOT fetch, does NOT own the row lists, and does NOT know
// what resource type it is on. The host screen owns all of that
// and simply tells ScopeTabs which tab is active, how many rows
// are in each, and whether the advisory should be shown.
//
// Why this exists as a component rather than seven inline
// implementations: the copy, the badge styling, the tab ordering,
// and the advisory banner's wording all need to be identical on
// every screen. Any drift would produce a mental-model mismatch
// between screens that all follow the same underlying rule. One
// file, one wording, one behaviour, everywhere.
//
// ============================================================
// Contract
// ============================================================
//
// Props:
//
//   activeTab             "tenant" | "app"                          (required)
//   onTabChange           (tab) => void                             (required)
//
//   tenantCount           int                                       (required)
//                          Number of rows currently in the Tenant Level list.
//                          Usually rendered as the row count; when the tenant
//                          has not published anything this is 0 and the tab
//                          still renders (a screen with no tenant data still
//                          needs the empty state reachable).
//
//   appCount              int                                       (required)
//                          Number of rows in the App Level list.
//
//   resourceNoun          string                                    (required)
//                          Singular, lowercase noun for the resource type:
//                            "config"       (APIConfigIndex, AuthIntegration)
//                            "chain"        (ChainApis)
//                            "connection"   (OAuthConnections)
//                            "action"       (ConnectorDefinitionsPanel)
//                          Used in the two tab descriptions and the
//                          advisory banner. Plural is derived by appending
//                          "s" — if a future resource type needs an irregular
//                          plural, add a `resourcePlural` prop and fall
//                          through to `${resourceNoun}s` when absent.
//
//   overriddenByAppLevel  bool                                      (optional, default false)
//                          When true, the screen is showing the Tenant Level
//                          tab AND an app-scoped resource of the same type
//                          exists on this app. Renders a soft advisory
//                          banner above the tab bar explaining that the
//                          tenant version is not being used while the
//                          app-level version exists, and that it's still
//                          attached (available as a fallback if the app-
//                          level resource is deleted).
//
//   attachCount           int                                       (optional, default 0)
//                          Number of tenant rows the app has explicitly
//                          attached. Used only in the advisory banner copy
//                          ("You've attached 3 of them"). Not used in the
//                          tab badge.
//
//   disabled              bool                                      (optional, default false)
//                          Renders both tabs greyed out and non-clickable.
//                          Used while the host screen's initial fetch is
//                          in flight, so a click during the loading window
//                          doesn't fire a fetch against a stale `activeTab`.
//
//   compact               bool                                      (optional, default false)
//                          Renders the compact pill variant with smaller
//                          padding. Used by screens where the tabs sit
//                          inside a narrow card (ChainApis modal header).
//
//   hiddenTabs            string[]                                  (optional, default [])
//                          List of tab IDs to hide entirely. Accepted values
//                          are "tenant" and "app". Used by global-context
//                          screens where the App tab doesn't apply (there
//                          is no app on this tenant), and by any future
//                          screen that wants to display a single-tier
//                          surface.
//
//                          ⚠️ When BOTH tabs are hidden, ScopeTabs renders
//                          nothing at all — a tab bar with zero tabs is
//                          meaningless. The host screen is responsible
//                          for not requesting that combination; the
//                          assertion below fails loud so a mistake in the
//                          caller is caught during render, not after.
//
//                          The hidden tab's count is ignored. If a host
//                          screen passes `hiddenTabs={["app"]}` and
//                          `appCount={5}`, the "5" is never rendered and
//                          the tab body is never reachable from this
//                          component. This is the desired behaviour — a
//                          global admin viewing tenant-only resources
//                          must not be told there are 5 app-scoped rows
//                          they can't see.
//
// ============================================================
// Layout
// ============================================================
//
// The component renders in this order:
//
//   1. Advisory banner (only when overriddenByAppLevel is true and
//      activeTab === "tenant"). Kept above the tab bar so it can be
//      read before the operator clicks anything in the tab body.
//
//   2. The two tabs, side by side. Each tab has:
//        - An icon (🏢 / 🏠)
//        - A short label ("Tenant Level" / "App Level")
//        - A count badge
//        - A one-line description underneath (small text)
//
//   3. Nothing else. The tab body is the host screen's job.
//
// The two-line tab layout (label + description) rather than a
// single-line pill is a deliberate choice: the semantic difference
// between "shared by the tenant" and "owned by this app" is the
// single most important concept on these screens, and it cannot
// be conveyed by "Tenant" vs. "App" alone. The description line
// doubles as the tooltip for mouse users, and it's the only thing
// on screen that the operator can read after coming back to a
// half-finished task.
//
// The advisory banner is only rendered on the Tenant tab. On the
// App tab, the tab body itself will say "This app has its own
// {noun} — the tenant version is not used" in its own empty state
// or header, so a second banner would be redundant noise.
//
// ============================================================
// Interaction rules
// ============================================================
//
// - Clicking the already-active tab is a no-op (does not re-fire
//   onTabChange). Prevents a redundant fetch on every click.
//
// - Clicking either tab while `disabled` is a no-op.
//
// - The tabs do not manage their own active state. They always
//   reflect the `activeTab` prop. This makes the tab bar a pure
//   function of the host screen's state — no possibility of the
//   tab bar and the body disagreeing about which tab is active.
//
// - Hiding a tab does not change which tab is active. A host
//   screen that starts with `activeTab="tenant"` and later adds
//   `"tenant"` to hiddenTabs is responsible for also setting
//   activeTab to the remaining visible tab. The assertion below
//   fails loud if the caller ends up with no visible tab to
//   display.
//
// ============================================================

import React from "react";
import { FaBuilding, FaHome } from "react-icons/fa";

const TAB_TENANT = "tenant";
const TAB_APP = "app";

const VALID_TABS = [TAB_TENANT, TAB_APP];

export default function ScopeTabs({
  activeTab,
  onTabChange,
  tenantCount,
  appCount,
  resourceNoun,
  overriddenByAppLevel = false,
  attachCount = 0,
  disabled = false,
  compact = false,
  hiddenTabs = [],
}) {
  // ── Guard: fail loud on caller mistakes ──
  //
  // activeTab and onTabChange are the only two props that the host
  // screen cannot derive. If either is missing, the screen is
  // broken and the failure should surface immediately rather than
  // silently rendering an inert tab bar.
  if (activeTab !== TAB_TENANT && activeTab !== TAB_APP) {
    throw new Error(
      `ScopeTabs: activeTab must be "${TAB_TENANT}" or "${TAB_APP}", got ${JSON.stringify(activeTab)}`,
    );
  }
  if (typeof onTabChange !== "function") {
    throw new Error("ScopeTabs: onTabChange must be a function");
  }

  // Validate hiddenTabs, and fail loud if the caller asks for an
  // impossible state (both tabs hidden, or an unknown tab id).
  const hiddenSet = new Set();
  if (hiddenTabs && Array.isArray(hiddenTabs)) {
    for (const t of hiddenTabs) {
      if (!VALID_TABS.includes(t)) {
        throw new Error(
          `ScopeTabs: hiddenTabs contains an unknown tab id ${JSON.stringify(t)}. ` +
            `Allowed: ${VALID_TABS.join(", ")}.`,
        );
      }
      hiddenSet.add(t);
    }
  } else if (hiddenTabs !== undefined && hiddenTabs !== null) {
    throw new Error(
      `ScopeTabs: hiddenTabs must be an array, got ${typeof hiddenTabs}`,
    );
  }

  const showTenantTab = !hiddenSet.has(TAB_TENANT);
  const showAppTab = !hiddenSet.has(TAB_APP);

  if (!showTenantTab && !showAppTab) {
    throw new Error(
      "ScopeTabs: both tabs are hidden. A tab bar with zero tabs is not a valid state — omit the component instead.",
    );
  }

  // If the active tab is hidden, that's the caller's bug. Fail
  // loud, because the alternative is a screen that renders nothing
  // selected and a body the user cannot reach.
  if (hiddenSet.has(activeTab)) {
    throw new Error(
      `ScopeTabs: activeTab is ${JSON.stringify(activeTab)} but that tab is in hiddenTabs. ` +
        `The host screen must set activeTab to a visible tab.`,
    );
  }

  const pluralNoun = `${resourceNoun}s`;

  const handleClick = (tab) => {
    if (disabled) return;
    if (tab === activeTab) return;
    onTabChange(tab);
  };

  // ── Inner render: one tab ──
  const renderTab = ({ tab, icon, label, description, count }) => {
    const isActive = activeTab === tab;
    const baseStyle = {
      display: "flex",
      alignItems: "flex-start",
      gap: compact ? 8 : 10,
      padding: compact ? "8px 12px" : "10px 16px",
      border: "none",
      borderRadius: 8,
      cursor: disabled ? "not-allowed" : isActive ? "default" : "pointer",
      backgroundColor: isActive ? "#ffffff" : "transparent",
      boxShadow: isActive
        ? "0 1px 3px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04)"
        : "none",
      opacity: disabled ? 0.55 : 1,
      textAlign: "left",
      flex: 1,
      minWidth: 0,
      transition: "background-color 0.15s ease, box-shadow 0.15s ease",
      userSelect: "none",
    };
    const iconStyle = {
      flexShrink: 0,
      color: isActive ? "#2b6cb0" : "#718096",
      marginTop: compact ? 1 : 2,
      fontSize: compact ? 12 : 14,
    };
    const labelStyle = {
      fontSize: compact ? 12 : 13,
      fontWeight: 600,
      color: isActive ? "#1a202c" : "#4a5568",
      display: "flex",
      alignItems: "center",
      gap: 6,
      lineHeight: 1.3,
    };
    const countStyle = {
      fontSize: compact ? 10 : 11,
      fontWeight: 600,
      color: isActive ? "#ffffff" : "#718096",
      backgroundColor: isActive ? "#2b6cb0" : "#e2e8f0",
      padding: compact ? "0 6px" : "1px 8px",
      borderRadius: 10,
      lineHeight: compact ? "16px" : "18px",
      minWidth: compact ? 18 : 22,
      textAlign: "center",
    };
    const descStyle = {
      fontSize: compact ? 10 : 11,
      color: isActive ? "#4a5568" : "#a0aec0",
      marginTop: 2,
      lineHeight: 1.4,
      overflow: "hidden",
      textOverflow: "ellipsis",
    };

    return (
      <button
        type="button"
        onClick={() => handleClick(tab)}
        disabled={disabled}
        aria-pressed={isActive}
        title={description}
        style={baseStyle}
        onMouseEnter={(e) => {
          if (!disabled && !isActive) {
            e.currentTarget.style.backgroundColor = "#ffffffcc";
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled && !isActive) {
            e.currentTarget.style.backgroundColor = "transparent";
          }
        }}
      >
        <span style={iconStyle}>{icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={labelStyle}>
            <span>{label}</span>
            <span style={countStyle}>{count}</span>
          </div>
          <div style={descStyle}>{description}</div>
        </div>
      </button>
    );
  };

  const showAdvisory =
    overriddenByAppLevel && activeTab === TAB_TENANT;

  return (
    <div style={{ marginBottom: 16 }}>
      {/* ============================================================ */}
      {/* Advisory banner — rendered only on the Tenant tab when an    */}
      {/* app-level resource exists. Explains why every row appears    */}
      {/* as "overridden" and reminds the operator that the tenant     */}
      {/* version is still attached (fallback is live).                */}
      {/* ============================================================ */}
      {showAdvisory && (
        <div
          role="note"
          style={{
            marginBottom: 10,
            padding: "10px 14px",
            backgroundColor: "#fff8e1",
            border: "1px solid #fde68a",
            borderLeft: "3px solid #d97706",
            borderRadius: 6,
            fontSize: 12,
            color: "#78350f",
            lineHeight: 1.55,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 2 }}>
            🏠 Your app has its own {resourceNoun}
          </div>
          <div>
            While your app-level {resourceNoun} exists, this app uses
            that one — the tenant {pluralNoun} below are not called.
            {attachCount > 0 ? (
              <>
                {" "}
                You&apos;ve attached {attachCount} of them; if you
                delete your app-level {resourceNoun}, this app will fall
                back to the attached tenant version.
              </>
            ) : (
              <>
                {" "}
                If you delete your app-level {resourceNoun}, this app
                will have no {resourceNoun} of this type until you attach
                one from below.
              </>
            )}
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* The tabs (one or both, depending on hiddenTabs)              */}
      {/* ============================================================ */}
      <div
        role="tablist"
        aria-label={`${resourceNoun} scope`}
        style={{
          display: "flex",
          gap: 4,
          padding: 4,
          backgroundColor: "#edf2f7",
          borderRadius: 10,
        }}
      >
        {showTenantTab &&
          renderTab({
            tab: TAB_TENANT,
            icon: <FaBuilding />,
            label: "Tenant Level",
            description: `Shared ${pluralNoun} published by your tenant admin.`,
            count: tenantCount,
          })}
        {showAppTab &&
          renderTab({
            tab: TAB_APP,
            icon: <FaHome />,
            label: "App Level",
            description: `Owned by this app. Configuring here overrides the tenant version.`,
            count: appCount,
          })}
      </div>
    </div>
  );
}

// ============================================================
// Convenience exports for the host screens
// ============================================================
//
// The strings are stable, so a host screen can compare against
// them without hardcoding the literal. Useful in JSX:
//
//   <ScopeTabs ... />
//   {activeTab === ScopeTabs.TAB_TENANT && <TenantList />}
//   {activeTab === ScopeTabs.TAB_APP    && <AppList />}
//
// The host's initial useState should use TAB_TENANT so operators
// see the shared library first (the attach affordance is more
// useful than "create your own" as a landing view — the create
// flow is one click either way, but attach/discover is what most
// operators actually need).

ScopeTabs.TAB_TENANT = TAB_TENANT;
ScopeTabs.TAB_APP = TAB_APP;